import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, AlertTriangle } from 'lucide-react';
import { usePayments } from '../../hooks/usePayments';
import { useTheme } from '../../context/ThemeContext';
import { getThemeClasses } from '../../utils/theme';
import PriorityBadge from '../common/PriorityBadge';
import RiskScore from '../common/RiskScore';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatCurrency, formatDate } from '../../utils/formatters';

const ReviewQueue = () => {
  const navigate = useNavigate();
  const { payments, loading } = usePayments();
  const { isDarkMode } = useTheme();
  const theme = getThemeClasses(isDarkMode);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const handleCaseClick = (caseId) => {
    navigate(`/case/${caseId}`);
  };

  if (loading) {
    return <LoadingSpinner message="Loading payment queue..." />;
  }

  // Filter payments based on selected filter and search query
  const filteredPayments = payments.filter((payment) => {
    // Filter by priority
    const matchesPriority =
      selectedFilter === 'all' ||
      payment.priority.toLowerCase() === selectedFilter;

    // Search by provider, id, or npi
    const matchesSearch =
      !searchQuery ||
      payment.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.npi?.includes(searchQuery);

    return matchesPriority && matchesSearch;
  });

  return (
    <div className={`${theme.card} rounded-xl border shadow-sm`}>
      {/* Queue Header */}
      <div className={`p-5 border-b ${theme.border}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Review Queue</h3>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 pr-4 py-2 ${theme.input} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none w-64`}
              />
            </div>

            {/* Filter */}
            <div className={`flex items-center gap-1 ${theme.bgTertiary} rounded-lg p-1`}>
              {['all', 'high', 'medium', 'low'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                    selectedFilter === filter
                      ? `${theme.bgSecondary} ${theme.textPrimary} shadow-sm`
                      : theme.textSecondary
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Queue Items */}
      <div className={`divide-y ${theme.borderLight}`}>
        {filteredPayments.map((payment) => (
          <div
            key={payment.id}
            onClick={() => handleCaseClick(payment.id)}
            className={`p-5 ${theme.cardHover} transition cursor-pointer group`}
          >
            <div className="flex items-start gap-4">
              {/* Risk Score */}
              <RiskScore score={payment.riskScore} />

              {/* Payment Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className={`font-semibold ${theme.textPrimary}`}>
                        {payment.provider}
                      </h4>
                      <PriorityBadge priority={payment.priority} />
                      <span className={`text-xs ${theme.textMuted} ${theme.bgTertiary} px-2 py-0.5 rounded`}>
                        {payment.auditType}
                      </span>
                    </div>
                    <div className={`flex items-center gap-4 mt-1 text-sm ${theme.textTertiary} flex-wrap`}>
                      <span>{payment.id}</span>
                      <span>•</span>
                      <span>NPI: {payment.npi}</span>
                      <span>•</span>
                      <span>{payment.region}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-semibold ${theme.textPrimary}`}>
                      {formatCurrency(payment.amount)}
                    </p>
                    <p className={`text-sm ${theme.textTertiary}`}>
                      {formatDate(payment.date)}
                    </p>
                  </div>
                </div>

                {/* Flag Reasons */}
                {payment.flagReasons && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-2">
                      {payment.flagReasons.map((reason, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center gap-1 text-xs ${theme.bgTertiary} ${theme.textSecondary} px-2 py-1 rounded`}
                        >
                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Procedure Codes */}
                {payment.procedureCodes && (
                  <div className={`flex items-center gap-2 mt-2 text-xs ${theme.textTertiary}`}>
                    <span>Codes:</span>
                    {payment.procedureCodes.map((code, idx) => (
                      <span
                        key={idx}
                        className={`font-mono ${theme.bgTertiary} px-1.5 py-0.5 rounded`}
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Arrow */}
              <div className="flex items-center self-center">
                <ChevronRight className={`w-5 h-5 ${theme.textMuted} group-hover:${theme.textSecondary} transition`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Queue Footer */}
      <div className={`p-4 border-t ${theme.border} ${theme.bgTertiary} rounded-b-xl`}>
        <div className={`flex items-center justify-between text-sm ${theme.textTertiary}`}>
          <span>
            Showing {filteredPayments.length} of {payments.length} flagged
            payments
          </span>
          <button className="text-blue-500 hover:text-blue-600 font-medium">
            Load more
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewQueue;
